import { alternateLinks, rootCanonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import {
  buttonClasses,
  buttonIconClasses,
} from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";
import {
  BellRing,
  Check,
  ChevronDown,
  Lock,
  RefreshCw,
  Smartphone,
} from "lucide-react";
import { ReactNode, useState } from "react";
import { Helmet } from "react-helmet-async";

import { AppWaitlistForm } from "./AppWaitlistForm";
import { PhoneShowcase } from "./PhoneShowcase";
import { AndroidIcon, AppleIcon } from "./PlatformIcons";

const IMPORT_POINTS = ["platforms", "nothingElse", "again"] as const;

const FEATURES = [
  { key: "onTheGo", Icon: Smartphone },
  { key: "alerts", Icon: BellRing },
  { key: "synced", Icon: RefreshCw },
] as const;

const FAQ_ITEMS = ["free", "launch", "account"] as const;

export const AppLandingPage = () => {
  const { t } = useTranslation("appLanding");
  const canonical = rootCanonicalUrl("/app");

  return (
    <main
      // The hero phone's glow reaches past the viewport on phones.
      className="flex-1 overflow-x-clip bg-primary-dark pb-24 text-neutral-100"
    >
      <Helmet>
        <title>{t("seo.title")}</title>
        <meta name="description" content={t("seo.description")} />
        <link rel="canonical" href={canonical} />
        {alternateLinks(canonical)}
      </Helmet>

      <section className="mx-auto flex max-w-[1200px] flex-col gap-14 px-4 pb-20 pt-14 mobile:px-6 touch-device:flex-row touch-device:items-center touch-device:pt-[88px]">
        <div className="flex min-w-0 flex-1 flex-col gap-6">
          <span className="self-start rounded-full bg-primary-action/12 px-3 py-1.5 text-xs font-semibold uppercase tracking-[.06em] text-primary-washed-blue">
            {t("hero.eyebrow")}
          </span>
          <h1 className="text-balance font-oswald text-[40px] font-bold uppercase leading-[1.02] text-neutral-50 mobile:text-5xl touch-device:text-[68px]">
            {t("hero.title")}
          </h1>
          <p className="max-w-[520px] text-pretty text-lg text-neutral-400">
            {t("hero.subtitle")}
          </p>
          <Card hoverable={false} className="max-w-[520px]">
            <AppWaitlistForm className="p-6" />
          </Card>
          <div className="flex items-center gap-3 text-sm font-semibold text-neutral-400">
            <AppleIcon className="size-5" />
            <AndroidIcon className="size-5" />
            <span>{t("hero.comingSoon")}</span>
          </div>
        </div>
        <PhoneShowcase />
      </section>

      <section
        aria-labelledby="club-import-title"
        className="border-y border-neutral-800 bg-neutral-900"
      >
        <div className="mx-auto flex max-w-[1200px] flex-col gap-14 px-4 py-20 mobile:px-6 touch-device:flex-row touch-device:items-center">
          <div className="flex min-w-0 flex-1 flex-col gap-5">
            <span className="text-xs font-semibold uppercase tracking-[.08em] text-blue-400">
              {t("clubImport.eyebrow")}
            </span>
            <SectionTitle id="club-import-title" large>
              {t("clubImport.title")}
            </SectionTitle>
            <p className="max-w-[480px] text-pretty text-lg text-neutral-400">
              {t("clubImport.body")}
            </p>
            <ul className="flex flex-col gap-3">
              {IMPORT_POINTS.map((key) => (
                <li
                  key={key}
                  className="flex items-center gap-3 text-neutral-200"
                >
                  <Check
                    aria-hidden
                    className="size-5 shrink-0 text-complete"
                  />
                  {t(`clubImport.points.${key}`)}
                </li>
              ))}
            </ul>
          </div>
          {/* Shows the button the app has; this one does nothing. */}
          <div
            aria-hidden
            className="flex flex-1 flex-col items-center gap-3 select-none"
          >
            <span className={buttonClasses({ size: "large", disabled: true })}>
              <span className={buttonIconClasses({ size: "large" })}>
                <Lock />
              </span>
              {t("clubImport.button")}
            </span>
            <span className="text-sm text-neutral-400">
              {t("clubImport.oneTap")}
            </span>
          </div>
        </div>
      </section>

      <section
        aria-labelledby="features-title"
        className="mx-auto flex max-w-[1200px] flex-col gap-9 px-4 py-20 mobile:px-6"
      >
        <SectionTitle id="features-title">{t("features.title")}</SectionTitle>
        <div className="grid grid-cols-1 gap-4 mobile:grid-cols-3">
          {FEATURES.map(({ key, Icon }) => (
            <Card key={key} className="flex flex-col gap-3 p-6">
              <Icon aria-hidden className="size-7 text-blue-400" />
              <h3 className="text-lg font-semibold text-neutral-100">
                <Trans
                  t={t}
                  i18nKey={`features.${key}.title`}
                  values={{ game: GAME_NAME }}
                  components={[
                    <em className="not-italic text-primary-washed-blue" />,
                  ]}
                />
              </h3>
              <p className="text-neutral-400">{t(`features.${key}.body`)}</p>
            </Card>
          ))}
        </div>
      </section>

      <section
        aria-labelledby="faq-title"
        className="mx-auto flex max-w-[800px] flex-col gap-6 px-4 mobile:px-6"
      >
        <SectionTitle id="faq-title">{t("faq.title")}</SectionTitle>
        <AppFaq />
      </section>
    </main>
  );
};

const SectionTitle = ({
  id,
  large,
  children,
}: {
  id: string;
  large?: boolean;
  children: ReactNode;
}) => (
  <h2
    id={id}
    className={cn(
      "text-balance font-oswald font-semibold uppercase leading-[1.04] text-neutral-50",
      large
        ? "text-4xl font-bold mobile:text-[56px]"
        : "text-[28px] mobile:text-[40px]",
    )}
  >
    {children}
  </h2>
);

/** Stands in for the app screenshot until the first build is ready to show. */

const AppFaq = () => {
  const { t } = useTranslation("appLanding");
  const [open, setOpen] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-2">
      {FAQ_ITEMS.map((key, i) => {
        const isOpen = open === i;
        return (
          <Card key={key} hoverable={false}>
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpen(isOpen ? null : i)}
              className="flex w-full items-center justify-between gap-4 px-5 py-[18px] text-left font-semibold text-neutral-100"
            >
              {t(`faq.items.${key}.question`)}
              <ChevronDown
                aria-hidden
                className={cn(
                  "size-5 shrink-0 text-neutral-400 transition-transform",
                  isOpen && "rotate-180",
                )}
              />
            </button>
            <Collapsible open={isOpen}>
              <p className="px-5 pb-[18px] text-neutral-400">
                {t(`faq.items.${key}.answer`)}
              </p>
            </Collapsible>
          </Card>
        );
      })}
    </div>
  );
};

export default AppLandingPage;
